import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@/lib/query";

import { useAuth } from "@/contexts/AuthContext";
import { isSupportedCountry } from "@/data/country";
import { countryQueryKey, getCountryData } from "@/services/location/country-data";
import { clearDraft, emptyDraft, loadDraft, saveDraft } from "@/services/onboarding/draft-store";
import { validateStep, type StepErrors, type StepId } from "@/services/onboarding/validation";
import type { CountryDataset, OnboardingDraft, SupportedCountry } from "@/types/onboarding";

export interface StepDefinition {
  id: StepId;
  title: string;
  /** Grouping shown in the wizard rail. */
  section: "Profile" | "Verification" | "Practice";
}

/** Order mirrors the onboarding document, grouped into three sections. */
export const ONBOARDING_STEPS: StepDefinition[] = [
  { id: "basics", title: "Basic details", section: "Profile" },
  { id: "specialization", title: "Specialization", section: "Profile" },
  { id: "education", title: "Education", section: "Profile" },
  { id: "practice", title: "How you practise", section: "Profile" },
  { id: "establishment", title: "Establishment", section: "Profile" },
  { id: "registration", title: "Medical registration", section: "Profile" },
  { id: "consultation", title: "Consultation", section: "Profile" },
  { id: "identity-proof", title: "Identity proof", section: "Verification" },
  { id: "registration-proof", title: "Registration proof", section: "Verification" },
  { id: "establishment-proof", title: "Establishment proof", section: "Verification" },
  { id: "location", title: "Map location", section: "Practice" },
  { id: "timings", title: "Timings", section: "Practice" },
  { id: "consultant", title: "Consultant details", section: "Practice" },
  { id: "review", title: "Review & submit", section: "Practice" },
];

interface OnboardingContextValue {
  draft: OnboardingDraft;
  update: (patch: Partial<OnboardingDraft>) => void;
  reset: () => void;
  steps: StepDefinition[];
  stepIndex: number;
  step: StepDefinition;
  errors: StepErrors;
  goTo: (index: number) => void;
  next: () => boolean;
  back: () => void;
  isFirst: boolean;
  isLast: boolean;
  progress: number;
  country: SupportedCountry | null;
  countryData: CountryDataset | undefined;
  countryLoading: boolean;
  hydrated: boolean;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({
  children,
  initial,
}: {
  children: React.ReactNode;
  initial?: Partial<OnboardingDraft> | undefined;
}) {
  const { uid } = useAuth();
  const [draft, setDraft] = useState<OnboardingDraft>(() => ({ ...emptyDraft(), ...initial }));
  const [hydrated, setHydrated] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [errors, setErrors] = useState<StepErrors>({});

  // localStorage is read after mount so SSR and the first client render match.
  useEffect(() => {
    setDraft({ ...loadDraft(uid), ...initial });
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  useEffect(() => {
    if (hydrated) saveDraft(uid, draft);
  }, [draft, hydrated, uid]);

  const update = useCallback((patch: Partial<OnboardingDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const next = { ...current };
      for (const key of Object.keys(patch) as (keyof OnboardingDraft)[]) delete next[key];
      return next;
    });
  }, []);

  const country = isSupportedCountry(draft.countryIso) ? draft.countryIso : null;

  const { data: countryData, isLoading: countryLoading } = useQuery({
    queryKey: countryQueryKey(country ?? "IN"),
    queryFn: () => getCountryData(country as SupportedCountry),
    enabled: Boolean(country),
    staleTime: Infinity,
  });

  const step = ONBOARDING_STEPS[stepIndex] as StepDefinition;

  const goTo = useCallback((index: number) => {
    setErrors({});
    setStepIndex(Math.max(0, Math.min(ONBOARDING_STEPS.length - 1, index)));
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const next = useCallback(() => {
    const found = validateStep(step.id, draft);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return false;
    }
    setDraft((current) => ({
      ...current,
      completedSteps: Array.from(new Set([...current.completedSteps, step.id])),
    }));
    if (stepIndex < ONBOARDING_STEPS.length - 1) goTo(stepIndex + 1);
    return true;
  }, [draft, goTo, step.id, stepIndex]);

  const back = useCallback(() => goTo(stepIndex - 1), [goTo, stepIndex]);

  const value = useMemo<OnboardingContextValue>(
    () => ({
      draft,
      update,
      reset: () => {
        clearDraft(uid);
        setDraft(emptyDraft());
        setStepIndex(0);
      },
      steps: ONBOARDING_STEPS,
      stepIndex,
      step,
      errors,
      goTo,
      next,
      back,
      isFirst: stepIndex === 0,
      isLast: stepIndex === ONBOARDING_STEPS.length - 1,
      progress: Math.round(((stepIndex + 1) / ONBOARDING_STEPS.length) * 100),
      country,
      countryData,
      countryLoading,
      hydrated,
    }),
    [
      back,
      country,
      countryData,
      countryLoading,
      draft,
      errors,
      goTo,
      hydrated,
      next,
      step,
      stepIndex,
      uid,
      update,
    ],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding(): OnboardingContextValue {
  const context = useContext(OnboardingContext);
  if (!context) throw new Error("useOnboarding must be used inside <OnboardingProvider>.");
  return context;
}
